import { moment } from "@msuite/utils/date";
import { calculateAspectRatio, PDF_ASPECT_RATIO, VIDEO_ASPECT_RATIO } from "@msuite/utils/files";
import { generateId, getFileName, getFileSizeString } from "@msuite/utils/strings";
import type { DocumentStructureFileItem, Tables } from "@msuite/utils/types";
import React from "react";
import { BlankSlate } from "../../blank-slate";
import { Button } from "../../button";
import { DataGrid } from "../../data-grid";
import { FileIcon } from "../../file-upload";
import type { Disclosure } from "../../hooks";
import { Icon } from "../../icons";
import { LightboxModal } from "../../lightbox";
import { useTranslation } from "../../localization";
import { toast } from "../../toast";
import { cn } from "../../utils";
import { useFileExplorerContext } from "./context";

/** Props Interface */
interface FileExplorerPreviewFileProps<T extends { file: Tables<"files"> }> {
	item: DocumentStructureFileItem<T>;
	disclosure: Disclosure;
}

export function FileExplorerPreviewFile<T extends { file: Tables<"files"> }>({
	item,
	disclosure: lightboxModal,
}: FileExplorerPreviewFileProps<T>) {
	/** Context */
	const { useRemoteFileUrl } = useFileExplorerContext();

	/** Hooks */
	const { signedUrl } = useRemoteFileUrl(item.file);
	const { t } = useTranslation();

	/** Computed */
	const itemId = generateId();
	const isImage = item.file.contentType.startsWith("image/");
	const isPdf = item.file.contentType === "application/pdf";
	const isVideo = item.file.contentType.startsWith("video/");

	/** Functions */
	function handleOnOpenItem() {
		if (!signedUrl) return;
		window.open(signedUrl);
	}

	function handleOnOpenLightbox() {
		if (!signedUrl || !isPdf) return;
		lightboxModal.onOpen();
	}

	function handleOnCopySignedUrl() {
		if (!signedUrl) return;
		navigator.clipboard.writeText(signedUrl);
		toast.info(t("general.copied_link"));
	}

	/** Render */
	return (
		<React.Fragment>
			<div
				key={signedUrl}
				className="w-full card overflow-hidden rounded-none p-0"
				onClick={lightboxModal.onOpen}
				style={{
					aspectRatio: isPdf
						? PDF_ASPECT_RATIO
						: isVideo
							? VIDEO_ASPECT_RATIO
							: calculateAspectRatio(item.file.width, item.file.height),
				}}
				data-lightbox-item="true"
				data-src={signedUrl || ""}
				data-height={item.file.height || ""}
				data-width={item.file.width || ""}
				data-content-type={item.file.contentType || ""}
				data-item-id={itemId}
				data-file-name={item.file.fileName || ""}
			>
				{signedUrl ? (
					<React.Fragment>
						{isImage ? (
							<img
								src={signedUrl || "https://via.placeholder.com/300"}
								alt={item.file?.fileName}
								onClick={(e) => e.stopPropagation()}
								className="cursor-default shrink-0"
								style={{
									maxHeight: "100%",
								}}
							/>
						) : isVideo ? (
							<video
								src={signedUrl || "https://via.placeholder.com/300"}
								controls={false}
								className="w-full"
								onClick={(e) => e.stopPropagation()}
							>
								Your browser does not support the video tag.
							</video>
						) : (
							<div
								onClick={handleOnOpenLightbox}
								className={cn(
									"cursor-pointer w-full h-full shrink-0 *:scale-[110%] overflow-hidden *:pointer-events-none",
									"relative",
									"hover:border-accent-500 border border-transparent",
								)}
							>
								<object
									data={`${signedUrl}#view=FitWidth&toolbar=0&navpanes=0&scrollbar=0`}
									className="w-full h-full overflow-hidden"
									type={item.file?.contentType}
								>
									<BlankSlate
										Icon={() => (
											<FileIcon
												fileType={item.file?.contentType}
												fileName={item.file?.fileName}
											/>
										)}
										label={`${t("files.open_file")}: ${item.file?.fileName}`}
										className="cursor-pointer w-full h-full"
										onClick={handleOnOpenItem}
										withAccessory={false}
										withBackground={false}
									/>
								</object>
							</div>
						)}
					</React.Fragment>
				) : null}
			</div>
			<DataGrid className="mt-4">
				<DataGrid.Row>
					<DataGrid.Label>{t("files.file_name")}</DataGrid.Label>
					<DataGrid.Value>{getFileName(item.file.fileName)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("files.full_path")}</DataGrid.Label>
					<DataGrid.Value>{item.file.fileName}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.created_at")}</DataGrid.Label>
					<DataGrid.Value>{moment(item.file.createdAt).format("LLLL")}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("files.file_size")}</DataGrid.Label>
					<DataGrid.Value>{getFileSizeString(item.file.size)}</DataGrid.Value>
				</DataGrid.Row>
				{item.file.width && item.file.height ? (
					<DataGrid.Row>
						<DataGrid.Label>{t("files.dimensions")}</DataGrid.Label>
						<DataGrid.Value>
							{item.file.width} x {item.file.height} px
						</DataGrid.Value>
					</DataGrid.Row>
				) : null}
				<DataGrid.Row>
					<DataGrid.Label>{t("files.signed_url")}</DataGrid.Label>
					<DataGrid.Cell>
						<div className="v-stack">
							<div className="w-full h-[90%] grow-0 flex">
								<a
									className="break-all line-clamp-1"
									href={signedUrl || ""}
									target="_blank"
									rel="noopener noreferrer"
								>
									{signedUrl}
								</a>
							</div>
							<Button
								icon={<Icon name="Link" />}
								onClick={handleOnCopySignedUrl}
							>
								{t("general.copy_link")}
							</Button>
						</div>
					</DataGrid.Cell>
				</DataGrid.Row>
			</DataGrid>
			<LightboxModal
				isOpen={lightboxModal.isOpen}
				onClose={lightboxModal.onClose}
				itemId={itemId}
				defaultIsGlobal
				withGlobalToggle={false}
			/>
		</React.Fragment>
	);
}
